/**
 * Autonomous Hub — top-level category hub for /autonomous.
 * Cross-references content/autonomous/*.md
 *
 * Lists 8 autonomous pages:
 *  /autonomous/adaptive-response, /autonomous/budget-optimization,
 *  /content-factory, /automated-decision-triggers,
 *  /monitoring, /performance-maximization,
 *  /autonomous/persona-networks, /autonomous/sentiment-mesh
 */

export const autonomousHubMeta = {
  title: "Perception X2 Autonomous Operations | Zero-Touch Perception Stack",
  description:
    "Eight autonomous operation surfaces: adaptive response, budget optimization, content factory, decision triggers, monitoring, performance maximization, persona networks, and sentiment mesh.",
  canonical: "https://perception.ac/autonomous",
  slug: "autonomous",
};

export interface AutonomousChild {
  code: string;
  title: string;
  description: string;
  href: string;
  icon: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export const autonomousChildren: AutonomousChild[] = [
  {
    code: "01",
    title: "Adaptive Response",
    description:
      "Real-time perception signal ingestion with autonomous counter-narrative deployment. Sub-second response on critical signals.",
    href: "/autonomous/adaptive-response",
    icon: "Zap",
    accent: "crimson",
  },
  {
    code: "02",
    title: "Budget Optimization",
    description:
      "Continuous reallocation of persona, content, and channel budgets against outcome KPIs. Closed-loop spend efficiency.",
    href: "/autonomous/budget-optimization",
    icon: "DollarSign",
    accent: "purple",
  },
  {
    code: "03",
    title: "Content Factory",
    description:
      "Multi-modal content production pipeline: text, image, and short-form video at machine speed, 95–98% organic authenticity.",
    href: "/content-factory",
    icon: "Factory",
    accent: "cyan",
  },
  {
    code: "04",
    title: "Decision Triggers",
    description:
      "Pre-built conditional logic that escalates, deploys, or pauses operations based on perception thresholds and outcome signals.",
    href: "/automated-decision-triggers",
    icon: "GitBranch",
    accent: "emerald",
  },
  {
    code: "05",
    title: "Monitoring",
    description:
      "Continuous 24/7 perception monitoring across 50+ platforms and 15+ languages. Signal-first ingestion with priority routing.",
    href: "/monitoring",
    icon: "Eye",
    accent: "amber",
  },
  {
    code: "06",
    title: "Performance Maximization",
    description:
      "Multi-armed bandit allocation across personas, narratives, channels, and timings. Continuous optimization toward the outcome ceiling.",
    href: "/performance-maximization",
    icon: "TrendingUp",
    accent: "denim",
  },
  {
    code: "07",
    title: "Persona Networks",
    description:
      "Adaptive persona fleets from 100 to 1M+. Continuous calibration against engagement signal. Topology-aware reinforcement.",
    href: "/autonomous/persona-networks",
    icon: "Users",
    accent: "violet",
  },
  {
    code: "08",
    title: "Sentiment Mesh",
    description:
      "Continuous 144-dimensional sentiment modeling with cross-language normalization. The nervous system of the autonomous stack.",
    href: "/autonomous/sentiment-mesh",
    icon: "Network",
    accent: "rose",
  },
];

export interface AutonomousStat {
  value: string;
  label: string;
  to: number;
  suffix?: string;
  decimals?: number;
  icon: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet";
}

export const autonomousStats: AutonomousStat[] = [
  { value: "8", label: "Autonomous Surfaces", to: 8, icon: "Bot", accent: "emerald" },
  { value: "47m", label: "Median Deploy", to: 47, suffix: "m", decimals: 0, icon: "Zap", accent: "crimson" },
  { value: "24/7", label: "Continuous Ops", to: 24, suffix: "/7", decimals: 0, icon: "Clock", accent: "cyan" },
  { value: "100%", label: "Autonomous Coverage", to: 100, suffix: "%", decimals: 0, icon: "Shield", accent: "amber" },
  { value: "1M+", label: "Persona Capacity", to: 1, suffix: "M+", decimals: 0, icon: "Users", accent: "purple" },
];

export interface AutonomousFeature {
  title: string;
  description: string;
  icon: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export const autonomousFeatures: AutonomousFeature[] = [
  {
    title: "Closed-Loop Feedback",
    description: "Continuous feedback between sensing, simulation, generation, and deployment — no manual handoffs.",
    icon: "RefreshCw",
    accent: "emerald",
  },
  {
    title: "Sub-Second Decision",
    description: "Critical perception signals trigger autonomous response in under a second. Sub-critical decisions in 60 seconds.",
    icon: "Zap",
    accent: "crimson",
  },
  {
    title: "Multi-Armed Bandit Allocation",
    description: "Continuous reallocation of personas, narratives, and channels against outcome KPIs — never leaving optimization on the table.",
    icon: "Sliders",
    accent: "purple",
  },
  {
    title: "Conditional Escalation",
    description: "Pre-built conditional logic escalates decisions to humans only when strategic thresholds are crossed.",
    icon: "GitBranch",
    accent: "cyan",
  },
  {
    title: "Adaptive Personas",
    description: "Persona fleets continuously recalibrate against engagement signal. Stale personas retire; high-performers amplify.",
    icon: "Users",
    accent: "amber",
  },
  {
    title: "Spend Efficiency",
    description: "Continuous budget reallocation against outcome metrics. The platform finds the marginal efficiency your manual process can't.",
    icon: "DollarSign",
    accent: "denim",
  },
  {
    title: "Multi-Modal Production",
    description: "Text, image, and short-form video content generated at machine speed, deployed without human review unless escalated.",
    icon: "Sparkles",
    accent: "violet",
  },
  {
    title: "Audit-Grade Lineage",
    description: "Every autonomous decision traceable to its inputs, thresholds, and outcome signals. SOC 2 Type II compliant.",
    icon: "FileCheck",
    accent: "rose",
  },
];

export interface AutonomousUseCase {
  title: string;
  body: string;
  icon: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export const autonomousUseCases: AutonomousUseCase[] = [
  {
    title: "Crisis-Class Response",
    body: "From emerging signal to deployed counter-narrative in under a minute. No human in the loop on sub-critical escalations.",
    icon: "Siren",
    accent: "crimson",
  },
  {
    title: "Always-On Brand Defense",
    body: "24/7 monitoring + adaptive response = a brand defense posture that never sleeps. Signals caught at 3 AM become action before sunrise.",
    icon: "Shield",
    accent: "emerald",
  },
  {
    title: "Performance Marketing Optimization",
    body: "Multi-armed bandit allocation across personas, narratives, channels, and timings — converging on the outcome ceiling in days, not quarters.",
    icon: "Target",
    accent: "purple",
  },
  {
    title: "Content at Machine Speed",
    body: "Multi-modal content factories producing text, image, and video at the pace the engagement demands — without quality compromise.",
    icon: "Sparkles",
    accent: "cyan",
  },
  {
    title: "Pre-Emptive Narrative Control",
    body: "Autonomous detection of emerging cascade patterns + pre-positioned counter-narratives = control before crystallization, not after.",
    icon: "GitBranch",
    accent: "amber",
  },
  {
    title: "Spend Efficiency at Scale",
    body: "Continuous budget reallocation against outcome KPIs — no over-spend on saturated channels, no under-investment in high-yield surfaces.",
    icon: "DollarSign",
    accent: "denim",
  },
];

export interface AutonomousWhy {
  title: string;
  body: string;
  metric?: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export const autonomousWhy: AutonomousWhy[] = [
  {
    title: "Zero-Touch Operations",
    body: "Eight surfaces composed under a closed-loop orchestration. Sub-critical decisions handled entirely without human input.",
    metric: "0-Touch",
    accent: "emerald",
  },
  {
    title: "Sub-Second Response",
    body: "Critical perception signals trigger autonomous response in under a second. The fastest operational cycle in perception management.",
    metric: "<1s",
    accent: "crimson",
  },
  {
    title: "Continuous Optimization",
    body: "Multi-armed bandit allocation converges on the outcome ceiling in days, not quarters. The platform never stops improving.",
    metric: "24/7",
    accent: "purple",
  },
  {
    title: "Conditional Escalation",
    body: "Humans are escalated only when strategic thresholds are crossed. Routine decisions never touch a human queue.",
    metric: "✓",
    accent: "cyan",
  },
  {
    title: "Spend Efficiency Gains",
    body: "Continuous reallocation finds marginal efficiency gains that manual allocation cannot — typically 2–4× spend efficiency lift.",
    metric: "3.4×",
    accent: "amber",
  },
  {
    title: "Audit-Grade Lineage",
    body: "Every autonomous decision traceable to its inputs and outcome signals. SOC 2 Type II and ISO 27001 compliant.",
    metric: "✓",
    accent: "denim",
  },
];

export interface AutonomousFaq {
  q: string;
  a: string;
}

export const autonomousFaq: AutonomousFaq[] = [
  {
    q: "What are the eight Perception X2 autonomous surfaces?",
    a: "The eight autonomous surfaces are: Adaptive Response (real-time counter-narrative deployment), Budget Optimization (continuous spend reallocation), Content Factory (multi-modal production), Decision Triggers (pre-built conditional logic), Monitoring (24/7 perception telemetry), Performance Maximization (multi-armed bandit allocation), Persona Networks (adaptive persona fleets), and Sentiment Mesh (144-dimensional sentiment modeling).",
  },
  {
    q: "How fast is the autonomous response on a critical signal?",
    a: "Critical perception signals trigger autonomous response in under a second from signal detection to counter-narrative deployment. Sub-critical signals are handled within 60 seconds. Strategic escalations are routed to human operators within 5 minutes with full context attached.",
  },
  {
    q: "What is multi-armed bandit allocation?",
    a: "Multi-armed bandit allocation is a continuous optimization technique that allocates resources across personas, narratives, channels, and timings against outcome KPIs. Unlike A/B testing which requires fixed arms, bandits continuously shift allocation toward the highest-yielding combinations in real time.",
  },
  {
    q: "Do humans ever need to be in the loop?",
    a: "Only on strategic escalations. The platform is configured with conditional escalation rules that route decisions to human operators only when strategic thresholds are crossed — for example, brand-safety violations, regulatory triggers, or material reputational risks. Routine decisions are handled entirely autonomously.",
  },
  {
    q: "How does the platform handle crisis scenarios?",
    a: "Crisis scenarios are handled by the Adaptive Response surface with pre-built playbooks for category-killing events. The platform can move from signal to deployed counter-narrative in under 60 minutes for crisis-class deployments, or under a second for autonomous critical-signal responses.",
  },
  {
    q: "How do you ensure the autonomous operations stay within bounds?",
    a: "Every autonomous surface operates within a defined policy envelope — ethical, brand-safety, regulatory, and strategic guardrails. Conditional escalation triggers route any decision that would exceed those bounds to a human operator. Audit-grade lineage captures every autonomous decision for compliance review.",
  },
  {
    q: "Can I customize the conditional escalation rules?",
    a: "Yes. Every engagement begins with a policy-design workshop where conditional escalation rules are defined against your organization's risk tolerance, brand safety requirements, and strategic posture. Rules can be adjusted at any time through the policy console.",
  },
  {
    q: "Does the platform learn from past decisions?",
    a: "Yes. Every autonomous engagement produces fresh training signal. The platform's reinforcement learning pipeline continuously improves decision quality across deployments. Outcome telemetry is fed back into the model registry to optimize future autonomous decisions.",
  },
  {
    q: "How is spend efficiency measured?",
    a: "Spend efficiency is measured as outcome (perception lift, conversion, resonance) per unit of operational cost. The platform tracks efficiency across personas, narratives, channels, and timings — and continuously reallocates spend to maximize the efficiency frontier.",
  },
  {
    q: "What happens if the platform detects a problem with an autonomous decision?",
    a: "The platform includes an automatic rollback mechanism — if a deployed decision is detected to be off-strategy, the platform reverts the deployment and routes the rollback to a human operator with full context. Audit-grade lineage captures the rollback for compliance review.",
  },
];

export const autonomousCta = {
  eyebrow: "Engage Autonomous Operations",
  title: "Eight surfaces. Zero-touch orchestration.",
  description:
    "The Perception X2 autonomous stack closes the loop across monitoring, decision, generation, and deployment. Begin with the surface that matches your operational priority — or commission the full autonomous orchestration.",
  primary: { label: "Request Autonomous Briefing", href: "/contact" },
  secondary: { label: "View Decision Triggers", href: "/automated-decision-triggers" },
  trustItems: [
    "8 autonomous surfaces",
    "Sub-second response",
    "24/7 operations",
    "Conditional escalation",
    "Audit-grade lineage",
  ],
};

/* Closed-loop stage data — bespoke visual */
export interface LoopStage {
  label: string;
  description: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export const loopStages: LoopStage[] = [
  { label: "Sense", description: "50+ platform signal ingestion at sub-second cadence", accent: "cyan" },
  { label: "Simulate", description: "Cascade probability modeling on incoming signals", accent: "purple" },
  { label: "Decide", description: "Conditional logic + multi-armed bandit allocation", accent: "amber" },
  { label: "Generate", description: "Multi-modal content at 95–98% organic authenticity", accent: "rose" },
  { label: "Deploy", description: "Topology-aware routing across persona networks", accent: "emerald" },
  { label: "Measure", description: "144-D perception telemetry + outcome attribution", accent: "denim" },
];

/* Decision-trigger tree — bespoke visual data */
export interface DecisionNode {
  label: string;
  children?: DecisionNode[];
}

export const decisionTree: DecisionNode = {
  label: "Perception Signal",
  children: [
    {
      label: "Critical",
      children: [
        { label: "Brand safety" },
        { label: "Regulatory" },
        { label: "Crisis-class cascade" },
      ],
    },
    {
      label: "Material",
      children: [
        { label: "Counter-narrative deploy" },
        { label: "Persona rebalance" },
        { label: "Spend reallocate" },
      ],
    },
    {
      label: "Sub-critical",
      children: [
        { label: "Auto-handle" },
        { label: "Background log" },
        { label: "Daily digest" },
      ],
    },
  ],
};
